import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Box,
  Typography,
  Tooltip
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';

const SalesTable = ({
  invoices,
  page,
  rowsPerPage,
  totalInvoices,
  onPageChange,
  onRowsPerPageChange,
  onViewInvoice,
  onPrintInvoice,
  onConvertDraftInvoice,
  onDeleteInvoice,
  onPayBalance,
  activeTab,
  phase2Enabled = false
}) => {
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'hold':
        return 'info';
      default:
        return 'default';
    }
  };

  const renderZatcaStatus = (zatcaStatus) => {
    if (!zatcaStatus || !zatcaStatus.submitted) {
      return (
        <Tooltip title="Not submitted to ZATCA">
          <RemoveIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        </Tooltip>
      );
    }

    // Show green checkmark if submitted successfully (ignore warnings)
    if (zatcaStatus.success && !zatcaStatus.hasErrors) {
      return (
        <Tooltip title={`Submitted: ${zatcaStatus.status}`}>
          <CheckCircleIcon 
            fontSize="small" 
            sx={{ 
              color: '#4caf50',
              '&:hover': { color: '#45a049' }
            }} 
          />
        </Tooltip>
      );
    }

    // Show red X if submission failed
    return (
      <Tooltip title={`Failed: ${zatcaStatus.status}`}>
        <CancelIcon 
          fontSize="small" 
          sx={{ 
            color: '#f44336',
            '&:hover': { color: '#d32f2f' }
          }} 
        />
      </Tooltip>
    );
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                fontWeight: 400, 
                color: 'text.secondary', 
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Invoice #</TableCell>
              <TableCell sx={{ 
                fontWeight: 400, 
                color: 'text.secondary', 
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Date</TableCell>
              <TableCell sx={{ 
                fontWeight: 400, 
                color: 'text.secondary', 
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Customer</TableCell>
              <TableCell sx={{ 
                fontWeight: 400, 
                color: 'text.secondary', 
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Mobile No</TableCell>
              <TableCell align="right" sx={{ 
                fontWeight: 400, 
                color: 'text.secondary', 
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Total Amount</TableCell>
              <TableCell align="right" sx={{ 
                fontWeight: 400, 
                color: 'text.secondary', 
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>VAT Amount</TableCell>
              <TableCell align="right" sx={{ 
                fontWeight: 400, 
                color: 'text.secondary', 
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Grand Total</TableCell>
              <TableCell align="right" sx={{ 
                fontWeight: 400, 
                color: 'text.secondary', 
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Balance</TableCell>
              <TableCell align="center" sx={{ 
                fontWeight: 400, 
                color: 'text.secondary', 
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Status</TableCell>
              {phase2Enabled && activeTab === 0 && (
                <TableCell align="center" sx={{ 
                  fontWeight: 400, 
                  color: 'text.secondary', 
                  py: 1.5,
                  borderBottom: '2px solid',
                  borderBottomColor: 'divider'
                }}>ZATCA</TableCell>
              )}
              <TableCell align="center" sx={{ 
                fontWeight: 400, 
                color: 'text.secondary', 
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {invoices.length > 0 ? (
              invoices.map((invoice) => (
                <TableRow 
                  key={invoice.id}
                  hover
                  sx={{ 
                    '&:last-child td': { borderBottom: 0 },
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" color="primary" fontWeight="medium">
                      {invoice.id}
                    </Typography>
                  </TableCell>
                  <TableCell>{invoice.date}</TableCell>
                  <TableCell>{invoice.customer}</TableCell>
                  <TableCell>{invoice.mobileNo}</TableCell>
                  <TableCell align="right">{Number(invoice.totalAmount).toFixed(2)}</TableCell>
                  <TableCell align="right">{Number(invoice.vatAmount).toFixed(2)}</TableCell>
                  <TableCell align="right">{Number(invoice.grandTotal).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      color={invoice.balanceAmount > 0 ? 'error.main' : 'success.main'}
                    >
                      {Number(invoice.balanceAmount || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={invoice.paymentStatus}
                      color={getStatusColor(invoice.paymentStatus)}
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        minWidth: '80px'
                      }}
                    />
                  </TableCell>
                  {phase2Enabled && activeTab === 0 && (
                    <TableCell align="center">
                      {renderZatcaStatus(invoice.zatcaStatus)}
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => onViewInvoice(invoice)}
                        sx={{ color: 'primary.main' }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onPrintInvoice(invoice)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                      {activeTab === 0 && invoice.balanceAmount > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => onPayBalance(invoice)}
                          sx={{ color: 'success.main' }}
                          title="Pay Balance"
                        >
                          <PaymentIcon fontSize="small" />
                        </IconButton>
                      )}
                      {activeTab === 1 && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => onConvertDraftInvoice(invoice)}
                            sx={{ color: 'success.main' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onDeleteInvoice(invoice)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={phase2Enabled && activeTab === 0 ? 11 : 10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {activeTab === 0 ? 'No invoices found' : 'No draft invoices available'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalInvoices}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{ 
          borderTop: '1px solid',
          borderTopColor: 'divider',
          '& .MuiTablePagination-select': {
            py: 0.5,
          },
          '& .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
          }
        }}
      />
    </Paper>
  );
};

export default React.memo(SalesTable); 